import React, { useState, useRef } from "react";
import { TextField, InputAdornment, IconButton, Popover, Box, Tooltip, useMediaQuery } from "@mui/material";
import { InsertEmoticon, AttachFile, Mic, Stop, FormatBold, Send, AutoAwesome } from "@mui/icons-material";
import ChannelSelector, { INTERNAL_NOTE_CHANNEL_ID } from "./ChannelSelector";
import { useTranslation } from "react-i18next";

const MessageInputField = ({
  message,
  setMessage,
  channelType,
  selectedChannel,
  setSelectedChannel,
  formatOptions,
  isRecording,
  startRecording,
  stopRecording,
  openFilePicker,
  handleEmojiPickerOpen,
  handleSendMessage,
  file,
  audioURL,
  onGenerateSuggestion,
  isGeneratingSuggestion = false,
  hasHistory = false,
}) => {
  const [formatAnchorEl, setFormatAnchorEl] = useState(null);
  const inputRef = useRef(null);
  const isMobile = useMediaQuery("(max-width: 768px)"); // Detecta se está no mobile
  const { t } = useTranslation("chat");
  const inputBorderRadius = 16; // evita efeito "cápsula" quando o campo cresce
  const canUseAiSuggestion =
    !!onGenerateSuggestion && channelType !== "Xpro" && hasHistory;
  const aiDisabled = isGeneratingSuggestion || !hasHistory;
  const hasFormatOptions = !!formatOptions?.[channelType]?.length;

  const handleFormatPickerOpen = (event) => setFormatAnchorEl(event.currentTarget);
  const handleFormatPickerClose = () => setFormatAnchorEl(null);

  const handleApplyFormat = (format) => {
    const input = inputRef.current;
    if (input) {
      const [startTag, endTag] = format.split("texto");
      const { selectionStart, selectionEnd } = input;

      if (selectionStart !== null && selectionEnd !== null) {
        const beforeText = message.substring(0, selectionStart);
        const selectedText = message.substring(selectionStart, selectionEnd);
        const afterText = message.substring(selectionEnd);

        setMessage(`${beforeText}${startTag}${selectedText}${endTag}${afterText}`);
      }
    }
    handleFormatPickerClose();
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? 0.5 : 1,
      }}
    >
      <TextField
        fullWidth
        multiline
        minRows={1}
        maxRows={4}
        variant="outlined"
        placeholder={
          selectedChannel === INTERNAL_NOTE_CHANNEL_ID || channelType === "Xpro"
            ? t("input.placeholderInternal")
            : channelType
            ? t("input.placeholderChannel", { channel: channelType })
            : t("input.placeholderGeneric")
        }
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault(); // impede quebra de linha
            handleSendMessage();
          }
        }}
        InputProps={{
          style: {
            // Valor fixo para o contorno não virar "cápsula" quando a altura crescer.
            borderRadius: inputBorderRadius,
            fontSize: isMobile ? "0.95rem" : "1rem",
          },
          inputRef: inputRef,
          endAdornment: !isMobile && ( // Exibe botões ao lado apenas no desktop
            <InputAdornment position="end">
              {canUseAiSuggestion && (
                <Tooltip
                  title={
                    !hasHistory
                      ? t("input.aiNoHistory")
                      : isGeneratingSuggestion
                      ? t("input.aiGenerating")
                      : t("input.aiSuggest")
                  }
                >
                  <span>
                    <IconButton
                      onClick={onGenerateSuggestion}
                      disabled={aiDisabled}
                    >
                      <AutoAwesome />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <IconButton onClick={handleEmojiPickerOpen}>
                <InsertEmoticon />
              </IconButton>
            {hasFormatOptions && (
              <IconButton onClick={handleFormatPickerOpen}>
                <FormatBold />
              </IconButton>
            )}
              <IconButton onClick={openFilePicker}>
                <AttachFile />
              </IconButton>
              <IconButton onClick={isRecording ? stopRecording : startRecording}>
                {isRecording ? <Stop /> : <Mic />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiInputBase-root": {
            borderRadius: `${inputBorderRadius}px !important`,
            overflow: "hidden",
            clipPath: `inset(0 round ${inputBorderRadius}px)`,
            "&::-webkit-scrollbar": {
              width: "5px", // Largura da barra de rolagem
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#b0b0b0", // Cor da barra
              borderRadius: "10px", // Arredondamento
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#888888", // Cor da barra ao passar o mouse
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1", // Cor do fundo do track
            },
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: `${inputBorderRadius}px !important`,
            overflow: "hidden", // garante clipping do conteúdo na borda arredondada
            clipPath: `inset(0 round ${inputBorderRadius}px)`,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: `${inputBorderRadius}px !important`,
          },
          "& textarea, & .MuiInputBase-inputMultiline": {
            overflowY: "auto",
            resize: "none",
            boxSizing: "border-box",
            borderRadius: "inherit",
            overflowX: "hidden",
            padding: "4px 14px",
            "&::-webkit-scrollbar": {
              width: "5px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#b0b0b0",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#888888",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
            },
          },
        }}
      />
      {isMobile && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 0.5,
            px: 0.5,
            gap: 0.5,
          }}
        >
          <ChannelSelector
            selectedChannel={selectedChannel}
            setSelectedChannel={setSelectedChannel}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              flexGrow: 1,
              justifyContent: "center",
            }}
          >
            {canUseAiSuggestion && (
              <IconButton
                size="small"
                onClick={onGenerateSuggestion}
                disabled={aiDisabled}
              >
                <AutoAwesome fontSize="small" />
              </IconButton>
            )}
            <IconButton size="small" onClick={handleEmojiPickerOpen}>
              <InsertEmoticon fontSize="small" />
            </IconButton>
            {hasFormatOptions && (
              <IconButton size="small" onClick={handleFormatPickerOpen}>
                <FormatBold fontSize="small" />
              </IconButton>
            )}
            <IconButton size="small" onClick={openFilePicker}>
              <AttachFile fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={isRecording ? stopRecording : startRecording}
            >
              {isRecording ? <Stop fontSize="small" /> : <Mic fontSize="small" />}
            </IconButton>
          </Box>
          <IconButton
            onClick={handleSendMessage}
            disabled={!message.trim() && !file && !audioURL}
            sx={{
              backgroundColor: "#3f51b5",
              color: "#fff",
              borderRadius: "50%",
              p: 1,
              ml: 0.5,
              "&:hover": { backgroundColor: "#303f9f" },
            }}
          >
            <Send fontSize="small" />
          </IconButton>
        </Box>
      )}
      {hasFormatOptions && (
        <Popover
          open={Boolean(formatAnchorEl)}
          anchorEl={formatAnchorEl}
          onClose={handleFormatPickerClose}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Box p={2} display="flex" gap={1}>
            {formatOptions[channelType]?.map((format, index) => (
              <Tooltip key={index} title={format.tooltip}>
                <IconButton onClick={() => handleApplyFormat(format.action)}>
                  {format.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        </Popover>
      )}
    </Box>
  );
};

export default MessageInputField;
